import { useDisplay } from 'vuetify';
import { appScale } from './appScale';

/**
 * Vuetify default thresholds (same as in vuetify/lib/composables/display.js).
 * These match the CSS @media breakpoints in VGrid.css.
 */
const THRESHOLDS = {
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920,
    xxl: 2560,
} as const;

/**
 * Scale-aware replacement for Vuetify's useDisplay().
 *
 * When the user pinch-zooms (scale < 1), the CSS `transform: scale()`
 * makes elements smaller, effectively fitting more content on screen.
 * The "virtual width" = realWidth / scale represents how many CSS pixels
 * the layout actually occupies.
 *
 * This composable recomputes breakpoint flags using virtualWidth so that
 * the UI can switch between mobile/desktop layouts based on how much
 * content actually fits, not just the physical viewport size.
 */
export function useScaledDisplay() {
    const display = useDisplay();

    const virtualWidth = computed(() => {
        const scale = appScale.value;
        // Only adjust when scale is actively reducing size (0 < scale < 1)
        if (scale > 0 && scale < 1) {
            return display.width.value / scale;
        }
        return display.width.value;
    });

    // Breakpoint flags based on virtual width
    const xs = computed(() => virtualWidth.value < THRESHOLDS.sm);
    const sm = computed(() => virtualWidth.value >= THRESHOLDS.sm && virtualWidth.value < THRESHOLDS.md);
    const md = computed(() => virtualWidth.value >= THRESHOLDS.md && virtualWidth.value < THRESHOLDS.lg);
    const lg = computed(() => virtualWidth.value >= THRESHOLDS.lg && virtualWidth.value < THRESHOLDS.xl);
    const xl = computed(() => virtualWidth.value >= THRESHOLDS.xl && virtualWidth.value < THRESHOLDS.xxl);
    const xxl = computed(() => virtualWidth.value >= THRESHOLDS.xxl);

    // "And up" helpers
    const smAndUp = computed(() => virtualWidth.value >= THRESHOLDS.sm);
    const mdAndUp = computed(() => virtualWidth.value >= THRESHOLDS.md);
    const lgAndUp = computed(() => virtualWidth.value >= THRESHOLDS.lg);
    const xlAndUp = computed(() => virtualWidth.value >= THRESHOLDS.xl);

    // "And down" helpers
    const smAndDown = computed(() => virtualWidth.value < THRESHOLDS.md);
    const mdAndDown = computed(() => virtualWidth.value < THRESHOLDS.lg);
    const lgAndDown = computed(() => virtualWidth.value < THRESHOLDS.xl);
    const xlAndDown = computed(() => virtualWidth.value < THRESHOLDS.xxl);

    // Breakpoint name
    const name = computed((): 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' => {
        if (xs.value) return 'xs';
        if (sm.value) return 'sm';
        if (md.value) return 'md';
        if (lg.value) return 'lg';
        if (xl.value) return 'xl';
        return 'xxl';
    });

    // Vuetify default mobileBreakpoint is 'lg' → threshold 1280
    const mobile = computed(() => virtualWidth.value < THRESHOLDS.lg);

    return {
        // Scale-aware breakpoint properties
        virtualWidth,
        xs,
        sm,
        md,
        lg,
        xl,
        xxl,
        smAndUp,
        mdAndUp,
        lgAndUp,
        xlAndUp,
        smAndDown,
        mdAndDown,
        lgAndDown,
        xlAndDown,
        mobile,
        name,
        scale: appScale,
        // Pass through original display properties for non-breakpoint uses
        width: display.width,
        height: display.height,
        platform: display.platform,
        thresholds: display.thresholds,
    };
}
